/**
 * @puffin-core/auth-client — passkey HTTP legs ONLY.
 *
 * The WebAuthn browser ceremony (`navigator.credentials.*`, base64url codecs)
 * is NOT here — it lives in `@puffin-core/mfa-ui` `WebAuthnService` (transport-
 * agnostic factory invariant: no `navigator`/`window`). The app composes the
 * ceremony with these HTTP legs.
 *
 * `authenticateWithPasskey` is the raw verify leg (no token storage — caller
 * stores); `authenticateWithPasskeyForContext` stores tokens + returns the
 * `{user, locations}` shape for `createAuthContext`.
 *
 * Both OPTIONS legs return the envelope data exactly as `@puffin-core/passkey`
 * `createPasskeyRouter` writes it — FLAT: `{ ...options, passkeyId }` for registration and
 * `{ ...options, challengeSessionId }` for discoverable sign-in. There is no nested `options`
 * key on the wire, so this module reads none (USER-FACING-GEMS L1: five app hooks read one and
 * failed every sign-in). The ONE sign-in decode is `@puffin-core/login-shell`
 * `authenticateWithDiscoverablePasskey`.
 *
 * @module @puffin-core/auth-client/operations/passkey
 */

import { extractData, authError, networkError } from '../internal.js';

const NETWORK_ERROR = 'Network error. Please check your connection.';

/**
 * @param {Object} ctx
 * @returns {Object} passkey methods
 */
export function buildPasskeyOperations(ctx) {
  const { request, endpoints, tokenStore, mustHaveToken, deviceTokenField } = ctx;

  async function getPasskeyRegisterOptions() {
    mustHaveToken();
    const res = await request({
      method: 'POST',
      path: endpoints.passkeyRegisterOptions,
      body: {},
      auth: 'storage',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR);
    if (!res.ok) throw authError(res, 'Failed to get registration options');
    return extractData(res.body);
  }

  async function registerPasskey(credential, friendlyName = '') {
    mustHaveToken();
    const res = await request({
      method: 'POST',
      path: endpoints.passkeyRegister,
      body: { credential, friendlyName },
      auth: 'storage',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR);
    if (!res.ok) throw authError(res, 'Passkey registration failed');
    return extractData(res.body);
  }

  async function getPasskeyDiscoverableOptions(companyId) {
    const res = await request({
      method: 'POST',
      path: endpoints.passkeyDiscoverableOptions,
      body: { companyId },
      auth: 'none',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR);
    if (!res.ok) throw authError(res, 'Failed to get authentication options');
    return extractData(res.body);
  }

  async function authenticateWithPasskey(credential, companyId, challengeSessionId) {
    const res = await request({
      method: 'POST',
      path: endpoints.passkeyAuthenticate,
      // The device this sign-in is made on (DRG-001), as a password sign-in carries it.
      body: { credential, companyId, challengeSessionId, ...deviceTokenField() },
      auth: 'none',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR);
    if (!res.ok) throw authError(res, 'Passkey authentication failed');
    return extractData(res.body);
  }

  async function authenticateWithPasskeyForContext(credential, companyId, challengeSessionId) {
    const rd = await authenticateWithPasskey(credential, companyId, challengeSessionId);
    tokenStore.setAuth({
      accessToken: rd.accessToken,
      csrfToken: rd.csrfToken,
      user: rd.user,
      locations: rd.locations,
    });
    return { user: rd.user, locations: rd.locations, accessToken: rd.accessToken };
  }

  async function getPasskeys() {
    mustHaveToken();
    const res = await request({ method: 'GET', path: endpoints.passkeyList, auth: 'storage' });
    if (res.networkError) throw networkError(NETWORK_ERROR);
    if (!res.ok) throw authError(res, 'Failed to load passkeys');
    return extractData(res.body);
  }

  async function deletePasskey(passkeyId) {
    mustHaveToken();
    const res = await request({
      method: 'DELETE',
      path: `${endpoints.passkeyDelete}/${passkeyId}`,
      auth: 'storage',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR);
    if (!res.ok) throw authError(res, 'Failed to delete passkey');
    return extractData(res.body);
  }

  return {
    getPasskeyRegisterOptions,
    registerPasskey,
    getPasskeyDiscoverableOptions,
    authenticateWithPasskey,
    authenticateWithPasskeyForContext,
    getPasskeys,
    deletePasskey,
  };
}
